// components/PackagesModal/PackagesModal.jsx

import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { getPackagesList } from "../../services/packagesApi";
import { useVideo } from "../../context/VideoContext";
import { useNotification } from "../../context/NotificationContext";
import DiscountCodeModal from "../Purchase/DiscountCodeModal";
import OrderSummaryModal from "../Purchase/OrderSummaryModal";
import { getPortalContainer } from "../../utils/portalContainer";
import "./PackagesModal.css";

const PackagesModal = ({ onClose, onSelectPackage }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // stateهای جدید برای فرآیند خرید
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [discountData, setDiscountData] = useState(null);

  const { isFullscreen: isVideoFullscreen, isIOS } = useVideo();
  const { showNotification } = useNotification();

  useEffect(() => {
    fetchPackages();
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await getPackagesList();

      if (response?.success && response?.data?.data) {
        const activePackages = response.data.data
          .filter((pkg) => pkg.packageId.status === true)
          .sort((a, b) => a.positionPakcage - b.positionPakcage);

        setPackages(activePackages);
      } else {
        setError("خطا در دریافت لیست پکیج‌ها");
      }
    } catch (err) {
      setError("خطا در ارتباط با سرور");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price, unit) => {
    const toman = price / 10;

    switch (unit) {
      case "thousandTomans":
        return `${(toman / 1000).toFixed(0)} هزار تومان`;
      case "millionTomans":
        return `${(toman / 1000000).toFixed(1)} میلیون تومان`;
      default:
        return `${toman.toLocaleString()} تومان`;
    }
  };

  // انتخاب پکیج
  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
    setShowDiscountModal(true);
  };

  // بازگشت از مودال تخفیف
  const handleBackFromDiscount = () => {
    setShowDiscountModal(false);
    setSelectedPackage(null);
    setDiscountData(null);
  };

  // ادامه از مودال تخفیف به مودال سفارش
  const handleContinueFromDiscount = (data) => {
    setDiscountData(data);
    setShowDiscountModal(false);
    setShowOrderModal(true);
  };

  // بازگشت از مودال سفارش
  const handleBackFromOrder = () => {
    setShowOrderModal(false);
    setShowDiscountModal(true);
  };

  // بستن همه مودال‌ها
  const handleCloseAll = () => {
    setShowDiscountModal(false);
    setShowOrderModal(false);
    setSelectedPackage(null);
    setDiscountData(null);
    onClose();
  };

  // دریافت اطلاعات کاربر (از طریق prop به کامپوننت‌های بعدی)
  const handleUserInfoLoaded = (info) => {
    setUserInfo(info);
  };

  const modalContent = (
    <>
      <div className="packages-modal-overlay">
        <div className="packages-modal" onClick={(e) => e.stopPropagation()}>
          <div className="packages-modal-header">
            <h3>خرید اشتراک</h3>
            <button className="close-btn" onClick={handleCloseAll}>
              ✕
            </button>
          </div>

          <div className="packages-modal-content">
            {loading && (
              <div className="packages-loading">
                <div className="spinner"></div>
                <p>در حال دریافت لیست پکیج‌ها...</p>
              </div>
            )}

            {error && (
              <div className="packages-error">
                <p>{error}</p>
                <button onClick={fetchPackages}>تلاش مجدد</button>
              </div>
            )}

            {!loading && !error && packages.length === 0 && (
              <div className="packages-empty">
                <p>پکیجی برای نمایش وجود ندارد</p>
              </div>
            )}

            {!loading && !error && packages.length > 0 && (
              <div className="packages-grid">
                {packages.map((pkg) => (
                  <div
                    key={pkg.packageId._id}
                    className={`package-card ${
                      pkg.typeOfPackage === "popular" ? "popular" : ""
                    }`}
                  >
                    {pkg.typeOfPackage === "popular" && (
                      <div className="popular-badge">
                        {pkg.subDisplayName || "پرفروش‌ترین"}
                      </div>
                    )}

                    <div className="package-title">
                      {pkg.packageId.displayName}
                    </div>

                    <div className="package-duration">
                      {pkg.packageId.validityDuration} روز
                    </div>

                    <div className="package-price">
                      {pkg.packageId.discountPercent > 0 && (
                        <>
                          <span className="old-price">
                            {formatPrice(
                              pkg.packageId.realPrice,
                              pkg.packageId.unitPriceDisplay,
                            )}
                          </span>
                          <span className="discount-badge">
                            -{pkg.packageId.discountPercent}%
                          </span>
                        </>
                      )}
                      <div className="final-price">
                        {formatPrice(
                          pkg.packageId.price,
                          pkg.packageId.unitPriceDisplay,
                        )}
                      </div>
                    </div>

                    <div className="package-features">
                      {pkg.packageId.features?.map((feature, idx) => (
                        <div key={idx} className="feature-item">
                          ✓ {feature}
                        </div>
                      ))}
                    </div>

                    <button
                      className="select-package-btn"
                      onClick={() => handleSelectPackage(pkg)}
                    >
                      {pkg.buttonText || "انتخاب"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* مودال کد تخفیف */}
      {showDiscountModal && selectedPackage && (
        <DiscountCodeModal
          isOpen={showDiscountModal}
          onClose={handleCloseAll}
          onBack={handleBackFromDiscount}
          selectedPackage={selectedPackage}
          onContinue={handleContinueFromDiscount}
          onUserInfoLoaded={handleUserInfoLoaded}
        />
      )}

      {/* مودال خلاصه سفارش */}
      {showOrderModal && selectedPackage && discountData && (
        <OrderSummaryModal
          isOpen={showOrderModal}
          onClose={handleCloseAll}
          onBack={handleBackFromOrder}
          selectedPackage={selectedPackage}
          discountData={discountData}
          userInfo={userInfo}
        />
      )}
    </>
  );

  const portalContainer = getPortalContainer(isIOS, isVideoFullscreen);
  return ReactDOM.createPortal(modalContent, portalContainer);
};

export default PackagesModal;
